import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Ban } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";
import type { CaseItem, User } from "@/types";

export const RestrictionBanner = () => {
  const { t } = usePrefs();
  const { user, refreshMe } = useAuth();
  const r = (user as User | null)?.booking_restricted;
  const [c, setC] = useState<CaseItem | null>(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!r?.active) return;
    api.get("/me/cases").then((res) => setC(res.data.find((x: CaseItem) => x.case_id === r.case_id) || null)).catch(() => {});
  }, [r?.active, r?.case_id]);
  if (!r?.active) return null;
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/cases/${r.case_id}/request`, { message: msg });
      setC(data);
      setMsg("");
      toast.success(t("b6.requestSent"));
      refreshMe();
    } catch (e) {
      toast.error(errorText(e, t));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="restriction-banner" className="rounded-3xl border-2 border-primary/40 bg-primary/5 p-4 space-y-3 mb-5">
      <p className="font-display font-black flex items-center gap-2 text-primary"><Ban className="h-5 w-5" />{t("b6.restrictedTitle")}</p>
      <p data-testid="restriction-message" className="text-sm">{t("b6.restrictedMsg")}</p>
      {c?.status === "rejected" && c.decision && <p data-testid="restriction-rejected" className="text-sm font-semibold">{t("b6.rejectedNote")}: {c.decision.reason}</p>}
      {c?.status === "awaiting_admin" ? (
        <p data-testid="restriction-awaiting" className="text-sm font-semibold text-amber-700 dark:text-amber-400">{t("b6.awaiting")}</p>
      ) : (
        <>
          <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} maxLength={1000} placeholder={t("b6.requestPh")}
            data-testid="restriction-request-input" className="w-full rounded-2xl border bg-background p-3 text-sm" />
          <button onClick={send} disabled={busy || msg.trim().length < 10} data-testid="restriction-request-submit"
            className="h-11 px-5 rounded-full bg-primary text-primary-foreground font-bold text-sm disabled:opacity-50">{busy ? t("common.loading") : t("b6.sendRequest")}</button>
        </>
      )}
    </div>
  );
};
